import React, { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";
import { Canvas, Textbox, Image, cache } from "fabric";
import Settings from "./Settings";
import krzyz1 from "../../../img/krzyz1.png";
import demo from "../../../img/demo.png";
import { Link as RouterLink } from "react-router-dom";
import TuneIcon from "@mui/icons-material/Tune";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import "../Guides/Guides.scss";
import "./Demo.scss";
import { Helmet } from "react-helmet-async";
import axios from "../../../axios";
import { GuideBackNav } from "../Guides/GuideLayout";

const MIN_DOWNLOAD_SPINNER_MS = 650;
const waitForNextPaint = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
const waitForMinimumSpinner = (startedAt) =>
  new Promise((resolve) =>
    setTimeout(resolve, Math.max(0, MIN_DOWNLOAD_SPINNER_MS - (Date.now() - startedAt))),
  );

// Czcionki wzoru – canvas nie czeka sam na ich wczytanie, więc bez tego rysuje zastępczą czcionkę
const DEMO_FONTS = [
  "25px Bitter",
  "italic 25px Bitter",
  "bold 18px Bitter",
  "bold 90px 'DM Serif Display'",
];

const DEMO_STEPS = [
  {
    title: "Kliknij element na podglądzie",
    text: "Zaznacz tekst lub krzyż. Po prawej stronie (na telefonie po kliknięciu „Edycja”) pojawią się jego opcje.",
  },
  {
    title: "Zmień treść",
    text: "Kliknij tekst dwukrotnie, aby wpisać własne imię i nazwisko, datę i miejsce pogrzebu.",
  },
  {
    title: "Dopasuj wygląd",
    text: "Zmień czcionkę, rozmiar, kolor i wyrównanie. Elementy przesuniesz, przeciągając je myszką lub palcem.",
  },
  {
    title: "Pobierz plik",
    text: "Zapisz nekrolog jako PDF do druku albo JPG do wysłania rodzinie lub publikacji w internecie.",
  },
];

const DEMO_VS_ACCOUNT = [
  {
    title: "W demo",
    items: [
      "jeden przykładowy wzór nekrologu",
      "edycja tekstów, czcionek i kolorów",
      "dodanie własnego obrazu",
      "pobranie PDF i JPG",
    ],
  },
  {
    title: "Po założeniu konta",
    items: [
      "dane zmarłego wpisujesz raz – nekrolog, klepsydra, szarfa i tabliczka powstają z jednego formularza",
      "wiele szablonów i własne wzory zakładu",
      "zdjęcie zmarłego z kadrowaniem, kolorem czarno-białym lub sepią i obwódką",
      "generator szarf pogrzebowych w wymiarze 1:1",
      "baza sentencji, kościołów i cmentarzy oraz konta dla pracowników",
    ],
  },
];

const FAQ = [
  {
    question: "Czy demo generatora nekrologów jest darmowe?",
    answer:
      "Tak. Demo działa bez logowania i rejestracji. Możesz edytować przykładowy nekrolog i pobrać go jako PDF lub JPG.",
  },
  {
    question: "Jak zmienić tekst w nekrologu?",
    answer:
      "Kliknij tekst na podglądzie dwukrotnie i wpisz nową treść. Czcionkę, rozmiar, kolor i wyrównanie zmienisz w panelu edycji obok podglądu.",
  },
  {
    question: "Czy nekrolog z demo nadaje się do druku?",
    answer:
      "Plik PDF ma format A4 w poziomie i wysoką rozdzielczość, więc możesz go wydrukować na zwykłej drukarce. W pełnej wersji dostępne są dodatkowe wzory i formaty.",
  },
  {
    question: "Czym różni się demo od pełnej wersji Nekrodruku?",
    answer:
      "Demo pokazuje jeden wzór nekrologu. Po założeniu konta dane zmarłego wpisujesz raz w formularzu, a program przygotowuje z nich nekrolog, klepsydrę, szarfę i tabliczkę na krzyż – z wieloma szablonami i zdjęciem.",
  },
];

const Demo = () => {
  const [, setIsLoading] = useState(true);
  const [buttonDisabledPDF, setButtonDisabledPDF] = useState(false);
  const [buttonDisabledJPG, setButtonDisabledJPG] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const isDownloading = buttonDisabledPDF || buttonDisabledJPG;

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [canvas, setCanvas] = useState(null);

  const originalWidth = 1000;
  const originalHeight = 707;

  const resizeCanvas = (fabricCanvas) => {
    if (!fabricCanvas || !fabricCanvas.lowerCanvasEl || !containerRef.current)
      return;

    const containerWidth = containerRef.current.offsetWidth;
    const scale = containerWidth / originalWidth;

    const newWidth = originalWidth * scale;
    const newHeight = originalHeight * scale;

    // Zoom
    fabricCanvas.renderAll();

    // lower-canvas
    fabricCanvas.lowerCanvasEl.style.width = `${newWidth}px`;
    fabricCanvas.lowerCanvasEl.style.height = `${newHeight}px`;

    // containerRef
    containerRef.current.style.height = `${newHeight}px`;

    // canvas-container
    const canvasContainer =
      containerRef.current.querySelector(".canvas-container");
    if (canvasContainer) {
      canvasContainer.style.width = `${newWidth}px`;
      canvasContainer.style.height = `${newHeight}px`;
    }

    // upper-canvas, kluczowy element
    const upperCanvas = containerRef.current.querySelector(".upper-canvas");
    if (upperCanvas) {
      upperCanvas.style.width = `${newWidth}px`;
      upperCanvas.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    setIsLoading(false);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!successMessage && !errorMessage) return undefined;

    const timeoutId = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 4500);

    return () => clearTimeout(timeoutId);
  }, [successMessage, errorMessage]);

  useEffect(() => {
    if (!canvasRef.current) return; // dodaj to

    // Zniszcz wcześniej istniejący canvas jeśli jest
    if (canvasRef.current.fabric) {
      canvasRef.current.fabric.dispose();
    }

    let isDisposed = false;
    const initCanvas = new Canvas(canvasRef.current, {
      width: originalWidth,
      height: originalHeight,
      useCORS: true,
      allowTaint: false,
      preserveObjectStacking: true,
    });

    // Dodaj teksty

    const textElements = [
      {
        text: "Bóg sam wybrał czas, by powołać Cię do siebie...",
        top: 100,
        left: (originalWidth - 640) / 2,
        fontSize: 25,
        width: 640,
        fontWeight: "normal",
        fontFamily: "Bitter",
        fontStyle: "italic",
      },
      {
        text: "Z głębokim żalem zawiadamiamy, że dnia 15.04.2024 r. odeszła od nas w wieku 84 lat nasza kochana Mama, Babcia i Prababcia",
        top: 148,
        left: (originalWidth - 600) / 2,
        fontSize: 22,
        width: 600,
        fontWeight: "normal",
        fontFamily: "Bitter",
      },
      {
        text: "śp. Hanna Kowalska",
        top: 280,
        left: (originalWidth * 0.1) / 2,
        fontSize: 90,
        width: originalWidth * 0.9,
        fontWeight: "bold",
        fontFamily: "DM Serif Display",
      },
      {
        text: "Msza św. żałobna zostanie odprawiona w czwartek 17.04.2024 r. o godzinie 12:00 w kościele pw. Św. Antoniego w Warszawie, po której nastąpi złożenie Ciała do grobu na cmentarzu parafialnym o godzinie 13:00.",
        top: 425,
        left: (originalWidth - originalWidth * 0.7) / 2,
        fontSize: 22,
        width: originalWidth * 0.7,
        fontWeight: "normal",
        fontFamily: "Bitter",
      },
      {
        text: "O czym zawiadamia pogrążona w smutku rodzina",
        top: 568,
        left: (originalWidth - 200) / 2,
        fontSize: 18,
        width: originalWidth * 0.5,
        fontWeight: "bold",
        fontFamily: "Bitter",
      },
    ];

    const textboxes = textElements.map(
      ({ text, top, left, fontSize, width, fontWeight, fontFamily, fontStyle = "normal" }) => {
        const textbox = new Textbox(text, {
          top,
          left,
          fontSize,
          textAlign: "center",
          width,
          fontWeight,
          fontFamily,
          fontStyle,
        });
        initCanvas.add(textbox);
        return textbox;
      },
    );

    // Po wczytaniu czcionek przelicz wymiary tekstów – wcześniej fabric mierzył zastępczą czcionkę
    if (document.fonts?.load) {
      Promise.all(DEMO_FONTS.map((font) => document.fonts.load(font)))
        .catch(() => undefined)
        .then(() => {
          if (isDisposed) return;
          cache.clearFontCache();
          textboxes.forEach((textbox) => {
            textbox.initDimensions();
            textbox.setCoords();
            // Obiekty tekstowe mają własny bufor obrazu – bez tego zostaje zastępcza czcionka
            textbox.set("dirty", true);
          });
          initCanvas.requestRenderAll();
        });
    }

    // Tło
    const imageElement = document.createElement("img");
    imageElement.crossOrigin = "anonymous";
    imageElement.src = demo;
    imageElement.onload = () => {
      if (isDisposed) return;
      const backgroundImage = new Image(imageElement);
      backgroundImage.scaleToWidth(originalWidth);
      backgroundImage.scaleToHeight(originalHeight);
      const bgWidth = backgroundImage.getScaledWidth();
      const bgHeight = backgroundImage.getScaledHeight();
      initCanvas.backgroundImage = backgroundImage;
      initCanvas.backgroundImage.set({
        left: (originalWidth - bgWidth) / 2,
        top: (originalHeight - bgHeight) / 2,
      });

      // Krzyż
      const crossImage = document.createElement("img");
      crossImage.crossOrigin = "anonymous";
      crossImage.src = krzyz1;
      crossImage.onload = () => {
        if (isDisposed) return;
        const image = new Image(crossImage);
        image.scaleToWidth(95);
        image.set({ left: 110, top: 95, selectable: true });
        initCanvas.add(image);
      };

      initCanvas.renderAll();
    };

    setCanvas(initCanvas);
    resizeCanvas(initCanvas);

    const handleResize = () => resizeCanvas(initCanvas);
    window.addEventListener("resize", handleResize);

    return () => {
      isDisposed = true;
      window.removeEventListener("resize", handleResize);
      initCanvas.dispose();
    };
  }, []);

  const handleDownloadPDF = async () => {
    if (!canvas) return;
    const startedAt = Date.now();
    let nextSuccessMessage = "";
    let nextErrorMessage = "";

    setButtonDisabledPDF(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await waitForNextPaint();
      await logger();
      canvas.discardActiveObject();
      canvas.renderAll();

      const imgData = canvas.toDataURL({
        format: "png",
        multiplier: 4,
        enableRetinaScaling: true,
      });

      const pdf = new jsPDF("landscape", "pt", "a4");
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save("nekrolog.pdf");
      nextSuccessMessage = "PDF został pobrany.";
    } catch (error) {
      console.error("Błąd przy generowaniu PDF:", error);
      nextErrorMessage = "Nie udało się pobrać PDF. Spróbuj ponownie.";
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledPDF(false);
      setSuccessMessage(nextSuccessMessage);
      setErrorMessage(nextErrorMessage);
    }
  };

  const handleDownloadJPG = async () => {
    if (!canvas) return;
    const startedAt = Date.now();
    let nextSuccessMessage = "";
    let nextErrorMessage = "";

    setButtonDisabledJPG(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await waitForNextPaint();
      await logger();
      canvas.discardActiveObject();
      canvas.renderAll();

      const imgData = canvas.toDataURL({
        format: "jpeg",
        quality: 1.0,
        multiplier: 4,
      });

      const link = document.createElement("a");
      link.href = imgData;
      link.download = "nekrolog.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      nextSuccessMessage = "JPG został pobrany.";
    } catch (error) {
      console.error("Błąd przy generowaniu JPG:", error);
      nextErrorMessage = "Nie udało się pobrać JPG. Spróbuj ponownie.";
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledJPG(false);
      setSuccessMessage(nextSuccessMessage);
      setErrorMessage(nextErrorMessage);
    }
  };

  const logger = async () => {
    try {
      await axios.get("/demo");
    } catch (error) {
      console.log("nie udało się");
    }
  };

  const pageTitle = "Generator nekrologów online – darmowe demo bez logowania | Nekrodruk.pl";
  const pageDescription =
    "Wypróbuj za darmo generator nekrologów online – bez logowania i rejestracji. Edytuj przykładowy nekrolog, zmień tekst, czcionkę i kolor, a potem pobierz go jako PDF lub JPG.";

  return (
    <main className="guide demoPage">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="generator nekrologów, generator nekrologów online, nekrolog online za darmo, demo nekrologu, nekrolog bez logowania, kreator nekrologów, klepsydra online, nekrolog do druku, nekrolog PDF"
        />
        <meta name="author" content="Nekrodruk.pl" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nekrodruk.pl/demo" />
        <link rel="canonical" href="https://nekrodruk.pl/demo" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: { "@type": "Answer", text: item.answer },
            })),
          })}
        </script>
      </Helmet>

      <section className="guide_hero demo_hero">
        <div className="guide_inner">
          <nav className="guide_breadcrumbs" aria-label="Ścieżka">
            <RouterLink to="/">Strona główna</RouterLink>
            <span aria-hidden="true">/</span>
            <span>Demo</span>
          </nav>
          <span className="guide_kicker guide_kicker--light">Demo bez logowania</span>
          <h1 className="guide_heroTitle">
            Generator nekrologów online<em> – wypróbuj za darmo</em>
          </h1>
          <p className="guide_heroLead">
            Edytuj przykładowy nekrolog tak, jak w programie: zmień treść, czcionkę i kolor, a gotowy plik
            pobierz jako PDF lub JPG. Bez rejestracji.
          </p>
        </div>
      </section>

      <section className="demo_workspace" aria-label="Generator nekrologu">
        <div className="guide_inner demo_grid">
          <div className="demoCard">
            <div className="demoCardHead">
              <div>
                <h2>Podgląd nekrologu</h2>
                <p>Kliknij element, aby go zaznaczyć. Dwuklik na tekście – edycja treści.</p>
              </div>
              {isMobile && (
                <button
                  type="button"
                  className="demoSettingsBtn"
                  onClick={() => setIsSettingsOpen(true)}
                >
                  <TuneIcon />
                  Edycja
                </button>
              )}
            </div>
            <div
              className="canvas-wrapper"
              ref={containerRef}
              style={{ width: "100%", maxWidth: "1000px", margin: "0 auto" }}
            >
              <canvas ref={canvasRef} />
            </div>
            <div className="btnsWrapper">
              <button
                className="btnDownload"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
              >
                {buttonDisabledPDF ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfOutlinedIcon />}
                Pobierz PDF
              </button>
              <button
                className="btnDownload btnDownload--outline"
                onClick={handleDownloadJPG}
                disabled={isDownloading}
              >
                {buttonDisabledJPG ? <CircularProgress size={18} color="inherit" /> : <ImageOutlinedIcon />}
                Pobierz JPG
              </button>
            </div>
            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                className="demoSuccessAlert"
              >
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                className="demoSuccessAlert"
              >
                {errorMessage}
              </Alert>
            )}
          </div>

          {canvas && (
            <aside className="demo_side">
              {isMobile && isSettingsOpen && (
                <div
                  className="settingsOverlay"
                  onClick={() => setIsSettingsOpen(false)}
                />
              )}
              <Settings
                canvas={canvas}
                isMobile={isMobile}
                isOpen={!isMobile || isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
              />
            </aside>
          )}
        </div>
      </section>

      <div className="guide_body">
        <section className="guide_section">
          <div className="guide_inner">
            <span className="guide_kicker">Krótka instrukcja</span>
            <h2 className="guide_title">Jak korzystać z demo?</h2>
            <ol className="guide_steps guide_steps--wide">
              {DEMO_STEPS.map((step) => (
                <li key={step.title}>
                  <strong>{step.title}</strong>
                  <span>{step.text}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="guide_section guide_section--soft">
          <div className="guide_inner demo_seo">
            <span className="guide_kicker">Nekrolog online</span>
            <h2 className="guide_title">Generator nekrologów dla zakładów pogrzebowych</h2>
            <p className="guide_lead">
              Nekrolog to zawiadomienie o śmierci z informacją o miejscu i godzinie pogrzebu – wywieszane na
              tablicach ogłoszeń, w kościele i przy domu zmarłego albo publikowane w internecie. Więcej o tym,
              co powinien zawierać, przeczytasz w poradniku{" "}
              <RouterLink to="/co-to-nekrolog" className="guide_inlineLink">co to jest nekrolog</RouterLink>.
            </p>
            <p className="guide_lead">
              Nekrodruk to generator nekrologów online, w którym zakład pogrzebowy przygotowuje nekrolog w kilka
              minut – bez programu graficznego i bez przepisywania danych. Wpisujesz imię i nazwisko, daty oraz
              miejsce mszy i pogrzebu, a program układa tekst na eleganckim wzorze gotowym do druku. Z tych samych
              danych powstaje też{" "}
              <RouterLink to="/klepsydra-wzor" className="guide_inlineLink">klepsydra</RouterLink>, napis na{" "}
              <RouterLink to="/drukowanie-szarf-pogrzebowych" className="guide_inlineLink">szarfę pogrzebową</RouterLink>{" "}
              i tabliczka na krzyż.
            </p>

            <div className="demo_compare">
              {DEMO_VS_ACCOUNT.map((column, index) => (
                <article
                  key={column.title}
                  className={`demo_compareCard${index === 1 ? " demo_compareCard--accent" : ""}`}
                >
                  <h3>{column.title}</h3>
                  <ul>
                    {column.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="guide_section">
          <div className="guide_inner">
            <div className="guide_callout">
              <div>
                <h2>
                  Prowadzisz zakład pogrzebowy?<em> Twórz nekrologi szybciej</em>
                </h2>
                <p>
                  Załóż konto i przygotuj nekrolog, klepsydrę, szarfę i tabliczkę z jednego formularza.
                </p>
              </div>
              <div className="guide_actions">
                <RouterLink to="/register" className="guide_btn guide_btn--light">
                  Załóż konto
                </RouterLink>
                <RouterLink to="/poradniki" className="guide_btn guide_btn--ghost">
                  Zobacz poradniki
                </RouterLink>
              </div>
            </div>
          </div>
        </section>

        <section className="guide_section guide_section--soft">
          <div className="guide_inner">
            <h2 className="guide_title">Najczęstsze pytania</h2>
            <div className="guide_faq">
              {FAQ.map((item) => (
                <details key={item.question} className="guide_faqItem">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <GuideBackNav />
      </div>
    </main>
  );
};

export default Demo;
